import { useCallback, useEffect, useRef, useState } from "react";
import {
  createUploadSession,
  uploadPoster,
  uploadVideoBlocks,
  completeUpload,
  deleteVideo,
} from "./videoApi";

// status: "idle" | "uploading" | "done" | "error"
const EMPTY = {
  status: "idle",
  progress: 0,                 // 0..1, blocks only
  error: null,                 // { code, message, quota }
  fileName: "",
  duration: 0,
  previewUrl: null,            // local blob: URL of the video (section A "local preview")
  posterPreviewUrl: null,      // local blob: URL of the chosen poster
  video: null,                 // { video_id, blob_url, poster_url } once API 4 succeeded
};

const releaseAll = (urlsRef) => {
  urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
  urlsRef.current = [];
};

const stopRun = (abortRef) => abortRef.current?.abort();

// One upload at a time. The SAS upload links stay inside startUpload and are never
// put in state, so they cannot reach the posts endpoint or the logs.
export default function useVideoUpload() {
  const [state, setState] = useState(EMPTY);
  const abortRef = useRef(null);
  const runRef = useRef(0);          // lets an old run know it was replaced or cancelled
  const videoIdRef = useRef(null);   // set as soon as API 1 answers, even if unfinished
  const emailRef = useRef("");
  const urlsRef = useRef([]);

  useEffect(() => {
    return () => {
      stopRun(abortRef);
      releaseAll(urlsRef);
    };
  }, []);

  // API 7. Frees the quota when the video was not published yet.
  const removeFromServer = useCallback(async () => {
    const id = videoIdRef.current;
    if (!id) return { ok: true };
    try {
      await deleteVideo(emailRef.current, id);
      videoIdRef.current = null;
      return { ok: true };
    } catch (err) {
      if (err.code === "VIDEO_BUSY") return { ok: false, code: err.code };
      videoIdRef.current = null;
      return { ok: false, code: err.code || "DELETE_FAILED" };
    }
  }, []);

  const startUpload = useCallback(
    async ({ email, plan, target, file, duration, poster }) => {
      // A failed or replaced earlier attempt must free its quota first.
      stopRun(abortRef);
      await removeFromServer();
      releaseAll(urlsRef);

      const run = ++runRef.current;
      const isCurrent = () => runRef.current === run;
      const controller = new AbortController();
      abortRef.current = controller;
      emailRef.current = email;

      const previewUrl = URL.createObjectURL(file);
      const posterPreviewUrl = poster ? URL.createObjectURL(poster) : null;
      urlsRef.current = [previewUrl, posterPreviewUrl].filter(Boolean);

      setState({
        ...EMPTY,
        status: "uploading",
        fileName: file.name,
        duration,
        previewUrl,
        posterPreviewUrl,
      });

      try {
        const session = await createUploadSession(
          { email, plan, target, file, duration },
          { signal: controller.signal }
        );
        videoIdRef.current = session.video_id;

        if (poster) {
          try {
            await uploadPoster(session, poster, { signal: controller.signal });
          } catch (err) {
            if (err.name === "AbortError") throw err;
            // The poster is optional in the API: carry on without it.
          }
        }

        await uploadVideoBlocks(session, file, {
          signal: controller.signal,
          onProgress: (progress) => {
            if (isCurrent()) setState((s) => ({ ...s, progress }));
          },
        });

        const done = await completeUpload(email, session.video_id, { signal: controller.signal });
        if (!isCurrent()) return;

        setState((s) => ({
          ...s,
          status: "done",
          progress: 1,
          video: {
            video_id: done.video_id,
            blob_url: done.blob_url,
            poster_url: done.poster_url || "",
          },
        }));
      } catch (err) {
        if (err.name === "AbortError" || !isCurrent()) return;
        setState((s) => ({
          ...s,
          status: "error",
          error: { code: err.code || "UNKNOWN", message: err.message, quota: err.quota },
        }));
      }
    },
    [removeFromServer]
  );

  // Cancel during the upload, or remove after it (section B).
  // Returns { ok } or { ok: false, code }. On VIDEO_BUSY nothing is cleared,
  // so the screen can show "This video is being published, try again in a few minutes".
  const discardVideo = useCallback(async () => {
    runRef.current += 1;
    stopRun(abortRef);
    const result = await removeFromServer();
    if (result.code !== "VIDEO_BUSY") {
      releaseAll(urlsRef);
      setState(EMPTY);
    }
    return result;
  }, [removeFromServer]);

  // Forget the video locally without calling the server (after the post is saved).
  const clearVideo = useCallback(() => {
    runRef.current += 1;
    stopRun(abortRef);
    videoIdRef.current = null;
    releaseAll(urlsRef);
    setState(EMPTY);
  }, []);

  return { ...state, startUpload, discardVideo, clearVideo };
}